import type { AppLanguage } from "~/i18n/languagePreference";

import { Paper } from "@mui/material";
import SwitchUI from "@mui/material/Switch";
import { ChangeEvent, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import ScanSoundToggle from "~/components/mobile/scanFeedback/ScanSoundToggle";
import SelectNative from "~/components/select/SelectNative";
import SettingRow from "~/components/settings/SettingRow";
import SettingSelectRow from "~/components/settings/SettingSelectRow";
import SettingsSection from "~/components/settings/SettingsSection";
import { TitleText } from "~/components/Typographies";
import useLanguagePreference from "~/hooks/useLanguagePreference";
import useScannerPreference from "~/hooks/useScannerPreference";
import AppThemeContext from "~/providers/AppThemeContext";
import DisplayModeSettings from "~settings/preferences/components/DisplayModeSettings";

const displayModeSectionId = "display-mode";

interface PreferencesProps {
	hosted?: boolean;
}

const Preferences = ({ hosted = false }: PreferencesProps) => {
	const { t } = useTranslation("common");
	const { setThemeSize, setThemeType, themeSize, themeType } = useContext(AppThemeContext);
	const { hash } = useLocation();
	const { hasUserRecord, language, setLanguage } = useLanguagePreference();
	const [materialScanEnabled, setMaterialScanEnabled] = useScannerPreference("material");
	const [availabilityScanEnabled, setAvailabilityScanEnabled] = useScannerPreference("availability");

	useEffect(() => {
		if (!hash) return;
		document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [hash]);

	const handleThemeChange = (event: ChangeEvent<HTMLInputElement>) => {
		setThemeType(event.target.checked ? "dark" : "light");
	};

	return (
		<Paper elevation={0} sx={{ p: 3 }}>
			{!hosted && (
				<TitleText bold sx={{ mb: 3 }}>
					{t("preferences.title")}
				</TitleText>
			)}

			<SettingsSection title={t("preferences.appearance.title")}>
				<SettingRow
					control={
						<SwitchUI
							checked={themeType === "dark"}
							onChange={handleThemeChange}
							slotProps={{ input: { "aria-label": t("preferences.appearance.darkThemeAria") } }}
						/>
					}
					label={t("preferences.appearance.darkTheme")}
				/>
				<SettingRow
					control={
						<SelectNative
							label=""
							noBlank
							onChange={(e) => setThemeSize(e.target.value)}
							options={[
								{ label: "1", value: 10 },
								{ label: "2", value: 12 },
								{ label: "3 (Default)", value: 14 },
								{ label: "4", value: 16 },
								{ label: "5", value: 18 },
								{ label: "6", value: 20 }
							]}
							size="small"
							sx={{ minWidth: 140 }}
							value={themeSize}
							variant="standard"
						/>
					}
					label={t("preferences.appearance.textSize")}
				/>
				<SettingSelectRow
					description={hasUserRecord ? undefined : t("preferences.appearance.languageDeviceOnlyNote")}
					label={t("preferences.appearance.language")}
					minWidth={200}
					noBlank
					onSave={(value) => setLanguage(value as AppLanguage)}
					options={[
						{ label: t("languages.en"), value: "en" },
						{ label: t("languages.es"), value: "es" }
					]}
					value={language}
				/>
			</SettingsSection>

			<SettingsSection
				description={t("preferences.displayMode.description")}
				id={displayModeSectionId}
				title={t("preferences.displayMode.title")}
			>
				<DisplayModeSettings />
			</SettingsSection>

			<SettingsSection
				description={t("preferences.mobileFeatures.description")}
				title={t("preferences.mobileFeatures.title")}
			>
				<SettingRow
					control={
						<SwitchUI
							checked={materialScanEnabled}
							onChange={(e) => setMaterialScanEnabled(e.target.checked)}
							slotProps={{ input: { "aria-label": t("preferences.mobileFeatures.materialScanner") } }}
						/>
					}
					description={t("preferences.mobileFeatures.materialScannerDescription")}
					label={t("preferences.mobileFeatures.materialScanner")}
				/>
				<SettingRow
					control={
						<SwitchUI
							checked={availabilityScanEnabled}
							onChange={(e) => setAvailabilityScanEnabled(e.target.checked)}
							slotProps={{ input: { "aria-label": t("preferences.mobileFeatures.availabilityScanner") } }}
						/>
					}
					description={t("preferences.mobileFeatures.availabilityScannerDescription")}
					label={t("preferences.mobileFeatures.availabilityScanner")}
				/>
				<SettingRow
					control={<ScanSoundToggle />}
					description={t("preferences.mobileFeatures.scanSoundDescription")}
					label={t("preferences.mobileFeatures.scanSound")}
				/>
			</SettingsSection>
		</Paper>
	);
};

export default Preferences;
